import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorGroup } from 'components/Display/UploadErrorModal/types';
import { UploadErrorModal } from 'components/Display/UploadErrorModal/UploadErrorModal';

import { CSVValidationErrorDetail } from '../helpers';

export type CSVErrorDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  errorDetails: Array<CSVValidationErrorDetail>;
};

export const CSVErrorDetailsModal: FC<CSVErrorDetailsModalProps> = ({ open, onClose, errorDetails }) => {
  const { t } = useTranslation();

  const errorGroups: Array<ErrorGroup> = useMemo(() => {
    const groups = new Map<string, Array<CSVValidationErrorDetail>>();

    errorDetails.forEach((error) => {
      if (!groups.has(error.column)) {
        groups.set(error.column, []);
      }
      groups.get(error.column)!.push(error);
    });

    return Array.from(groups.entries()).map(([column, errors]) => ({
      columnLabel: column,
      errorCount: errors.length,
      rows: errors
        .sort((a, b) => a.row - b.row)
        .map((error) => {
          const formatKey = `partners:request.newBulkTradePartner.form.upload.errorDetails.formats.${error.expectedFormat}`;
          const errorMessage = t(formatKey, { columns: error.column, value: error.value });

          return {
            row: error.row,
            columns: [{ value: String(error.row) }, { value: errorMessage }],
          };
        }),
    }));
  }, [errorDetails, t]);

  const columnHeaders = [
    { label: t('partners:request.newBulkTradePartner.form.upload.errorDetails.row'), className: 'w-20' },
    { label: t('partners:request.newBulkTradePartner.form.upload.errorDetails.expectedFormat') },
  ];

  return (
    <UploadErrorModal
      open={open}
      onClose={onClose}
      title={t('partners:request.newBulkTradePartner.form.upload.errorDetails.modalTitle')}
      description={t('partners:request.newBulkTradePartner.form.upload.errorDetails.modalDescription', {
        count: errorDetails.length,
      })}
      errorGroups={errorGroups}
      columnHeaders={columnHeaders}
    />
  );
};

/**
 * i18n
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.required')
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.onlyOne')
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.atLeastOneRequired')
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.invalidGln')
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.ownGln')
 * t('partners:request.newBulkTradePartner.form.upload.errorDetails.formats.duplicateGln')
 */
